// SPEC-COLLECTION-HISTORY-FILTER-AND-ROWCAP-FIX-01 (D-395) ITEM_3 — 集金履歴フィルタの純粋ロジック。
//   期間(JST)/エリア/ブランド(A案)/店舗チェックの解決を1箇所に集約(UIとテストの両方から使う)。
import { brandNamesForFilter } from './storeBrandGroups';

// JST 暦日 YYYY-MM-DD。★toISOString は使わない(UTCずれで前日/翌日に飛ぶ=ITEM_3_FILTER_PERIOD)。
//   sv-SE + Asia/Tokyo で JST の暦日を得る(既存の日付規約と同一)。
export function jstDateString(value) {
  if (!value) return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('sv-SE', { timeZone: 'Asia/Tokyo' });
}

// area/brand で店舗一覧を絞る(=店舗チェックの候補。AC7: エリアを選ぶと候補がそのエリアに絞られる)。
//   stores: [{ store_code, store_name, area, brand_name }]。area/brandRep 未指定はその軸で絞らない。
export function storeOptionsForFilter(stores, { area = null, brandRep = null } = {}) {
  const brandSet = brandRep ? new Set(brandNamesForFilter(brandRep)) : null;
  return (stores ?? []).filter(
    (s) => (!area || s.area === area) && (!brandSet || brandSet.has(s.brand_name)),
  );
}

// SPEC-COLLECTION-HISTORY-STORE-SELECT-01 (D-960): 店舗候補の並び順。
//   坂本さん報告: 店舗チェック行が横スクロールで、店舗数が多いと最初の数店しか見えず選べることに気づけない。
//   対処は別D-960 ITEM(店舗を選べる select を足す)。ここは並び順のみ担当。
//   ★並びは「店舗名の見た目の五十音順」であって DB 取得順やコード順ではない。
//     stores.locality_kana は他画面(ClawsupportHub/StockHub/TanasupportHub/PatrolRoutePage 等)が
//     店舗の50音キーとして実際に使っている列(店名そのものの読みではなく所在地の読みだが、
//     このコードベースでは店舗の並び替えキーとして採用されているのがこの列のみ=SSOT)。
//     あればそれを使い、無ければ store_name そのものを localeCompare('ja') で比較する
//     (「カナ列があれば使う」= locality_kana、無ければ日本語ロケール比較のフォールバック)。
export function sortStoresJa(stores) {
  return [...(stores ?? [])].sort((a, b) => {
    const ak = a?.locality_kana || a?.store_name || '';
    const bk = b?.locality_kana || b?.store_name || '';
    return ak.localeCompare(bk, 'ja');
  });
}

// D-1062: 集金の行 [{ store_code, executed_at }] → { store_code: 最大の executed_at }。
//   坂本さん要望(app_feedback ee31dd1c)「集金履歴の店舗表示の順番を最新の実行日順に」。
//   ★取消を除くのは呼ぶ側(getLatestCollectionDateByStore が status<>'cancelled' で取る)。
//   ★比較は時刻(Date.parse)。文字列のままだとオフセットが違う値で順序を誤る。
export function latestExecutedAtByStore(rows) {
  const out = {};
  for (const r of rows ?? []) {
    const code = r?.store_code;
    const t = Date.parse(r?.executed_at ?? '');
    if (!code || Number.isNaN(t)) continue;
    if (!(code in out) || t > Date.parse(out[code])) out[code] = r.executed_at;
  }
  return out;
}

// D-1062: 店舗候補の並び = 最新の実行日(executed_at)が新しい店が先。
//   集金が無い店(= latestByStoreCode に無い、または日時として読めない)は後ろ。
//   その中と、同じ時刻どうしは sortStoresJa(D-960 の五十音順)。元配列は壊さない。
export function sortStoresByLatest(stores, latestByStoreCode) {
  const latest = latestByStoreCode ?? {};
  const timeOf = (s) => {
    const t = Date.parse(latest[s?.store_code] ?? '');
    return Number.isNaN(t) ? null : t;
  };
  const ja = sortStoresJa(stores);
  const rank = new Map(ja.map((s, i) => [s, i]));
  return ja.sort((a, b) => {
    const ta = timeOf(a);
    const tb = timeOf(b);
    if (ta != null && tb != null && ta !== tb) return tb - ta;
    if (ta != null && tb == null) return -1;
    if (ta == null && tb != null) return 1;
    return rank.get(a) - rank.get(b);
  });
}

// フィルタ最終形 → 対象 store_code 群。
//   返り値 null = 店舗制約なし(全店。AC9 の未指定時に対応)。
//   返り値 [] = 制約はあるが該当店ゼロ(=何も表示しない)。
//   area/brand で候補を絞り、その上でチェックされた店だけに更に絞る(チェック未指定=候補全部)。
export function resolveStoreCodes(stores, { area = null, brandRep = null, checkedStoreCodes = null } = {}) {
  const hasChecked = Array.isArray(checkedStoreCodes) && checkedStoreCodes.length > 0;
  if (!area && !brandRep && !hasChecked) return null; // 制約なし=全店(従来表示)
  const candidates = storeOptionsForFilter(stores, { area, brandRep }).map((s) => s.store_code);
  if (hasChecked) {
    const checked = new Set(checkedStoreCodes);
    return candidates.filter((c) => checked.has(c));
  }
  return candidates;
}

// 期間フィルタが有効かつ両端を含むかの正規化。start/end は 'YYYY-MM-DD'(JST 暦日)想定、null 可。
export function normalizePeriod({ startDate = null, endDate = null } = {}) {
  return { startDate: startDate || null, endDate: endDate || null };
}
